import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectionPickerDialog, type SelectionValue } from '@/components';
import { useUsers } from '../hooks';
import type { UserListItem } from '../types';
import useDebounce from '@/utils/useDebounce';

const PICKER_PAGE_SIZE = 25;

interface UserPickerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (user: UserListItem) => void;
  selectedUserId?: string;
}

/**
 * Dialog that lets an admin search all users and pick one.
 * Reuses the same user list endpoint as the users overview page.
 */
export function UserPickerDialog({
  isOpen,
  onClose,
  onSelect,
  selectedUserId,
}: UserPickerDialogProps) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput);

  // The dialog stays mounted while closed, so the list is only fetched once it opens.
  const { data, isLoading } = useUsers(
    { page: 1, pageSize: PICKER_PAGE_SIZE, search: debouncedSearch || undefined },
    { enabled: isOpen }
  );

  const values: SelectionValue<UserListItem>[] = useMemo(
    () =>
      (data?.data ?? []).map((user) => ({
        id: user.id,
        value: user.username,
        subValue: user.email,
        obj: user,
      })),
    [data]
  );

  const handleSelect = (_id: string, user?: UserListItem) => {
    if (!user) return;
    onSelect(user);
    onClose();
  };

  return (
    <SelectionPickerDialog<UserListItem>
      isOpen={isOpen}
      onClose={onClose}
      header={t('users.picker.title')}
      subheader={t('users.picker.description')}
      isLoading={isLoading}
      values={values}
      isUpdating={false}
      onSelect={handleSelect}
      selectedId={selectedUserId}
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder={t('users.picker.searchPlaceholder')}
      emptyText={t('users.picker.noResults')}
    />
  );
}
